// ABOUTME: Admin backup management interface with status dashboard, manual backup creation, and history table
// ABOUTME: Full-featured version with backup history, download, delete functionality

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Collapse,
  Snackbar,
  Pagination,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Download,
  Delete,
  ExpandMore,
  ExpandLess,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createBackup, getBackupStatus, getScheduledBackupStatus, deleteBackup, downloadBackup, getBackupHistory } from '../services/api';

interface BackupStatusResponse {
  lastBackupDate: string | null;
  lastBackupType: string | null;
  familyCount: number;
  userCount: number;
  childCount: number;
  totalSize?: number;
  gitCommit?: string;
  gitBranch?: string;
  // Google Drive fields
  googleDriveFileId?: string;
  googleDriveUploadTime?: string;
  googleDriveFolderPath?: string;
  googleDriveError?: string;
}

interface BackupRecord {
  id: string;
  filename: string;
  createdAt: string;
  size: number;
  status: 'Success' | 'Failed';
  type: 'Manual' | 'Scheduled';
  duration?: number;
  createdBy: string;
}

interface BackupHistoryResponse {
  data: BackupRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface ScheduledBackupStatusResponse {
  isRunning: boolean;
  lastRunTime: string | null;
  nextRunTime: string | null;
  lastRunSuccessful: boolean;
  lastRunError: string | null;
  totalBackupsCreated: number;
  consecutiveFailures: number;
  googleDriveEnabled: boolean;
  googleDriveConnected: boolean;
  serviceStartedAt: string;
}

const AdminSystemBackups: React.FC = () => {
  const navigate = useNavigate();
  const [backupStatus, setBackupStatus] = useState<BackupStatusResponse | null>(null);
  const [scheduledBackupStatus, setScheduledBackupStatus] = useState<ScheduledBackupStatusResponse | null>(null);
  const [backupHistory, setBackupHistory] = useState<BackupHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState<BackupRecord | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupRecord | null>(null);
  
  // Card expansion state
  const [expandedCards, setExpandedCards] = useState({
    status: true,
    backup: true,
    history: true
  });

  // Filtering state
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Load backup status
  const loadBackupStatus = useCallback(async () => {
    try {
      const status = await getBackupStatus();
      setBackupStatus(status);
      setError(null);
    } catch (err) {
      setError('Failed to load backup status');
      console.error('Failed to load backup status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load scheduled backup status
  const loadScheduledBackupStatus = useCallback(async () => {
    try {
      const status = await getScheduledBackupStatus();
      setScheduledBackupStatus(status);
    } catch (err) {
      console.error('Error loading scheduled backup status:', err);
    }
  }, []);

  // Load backup history
  const loadBackupHistory = useCallback(async () => {
    try {
      const history = await getBackupHistory(currentPage, itemsPerPage);
      setBackupHistory(history);
    } catch (err) {
      console.error('Failed to load backup history:', err);
    }
  }, [currentPage, itemsPerPage]);

  // Initial load
  useEffect(() => {
    loadBackupStatus();
    loadScheduledBackupStatus();
    loadBackupHistory();
  }, [loadBackupStatus, loadScheduledBackupStatus, loadBackupHistory]);

  // Polling for real-time updates
  useEffect(() => {
    const intervalId = setInterval(() => {
      loadBackupStatus();
      loadScheduledBackupStatus();
      loadBackupHistory();
    }, 30000); // Poll every 30 seconds
    return () => clearInterval(intervalId);
  }, [loadBackupStatus, loadScheduledBackupStatus, loadBackupHistory]);

  // Load user preferences for card expansion
  useEffect(() => {
    const savedPreferences = localStorage.getItem('admin-backup-card-preferences');
    if (savedPreferences) {
      setExpandedCards(JSON.parse(savedPreferences));
    }
  }, []);

  // Save user preferences
  const saveCardPreferences = (newPreferences: typeof expandedCards) => {
    setExpandedCards(newPreferences);
    localStorage.setItem('admin-backup-card-preferences', JSON.stringify(newPreferences));
  };

  const toggleCard = (cardName: keyof typeof expandedCards) => {
    const newPreferences = { ...expandedCards, [cardName]: !expandedCards[cardName] };
    saveCardPreferences(newPreferences);
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      await createBackup();
      setSnackbarMessage('Backup created successfully');
      setSnackbarOpen(true);
      await loadBackupStatus();
      await loadScheduledBackupStatus();
      await loadBackupHistory();
    } catch (err) {
      setSnackbarMessage('Failed to create backup');
      setSnackbarOpen(true);
      console.error('Failed to create backup:', err);
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleDownload = async (backup: BackupRecord) => {
    try {
      const blob = await downloadBackup(backup.filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backup.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setSnackbarMessage('Failed to download backup');
      setSnackbarOpen(true);
      console.error('Failed to download backup:', err);
    }
  };

  const handleDeleteClick = (backup: BackupRecord) => {
    setBackupToDelete(backup);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!backupToDelete) return;

    try {
      await deleteBackup(backupToDelete.filename);
      setSnackbarMessage('Backup deleted successfully');
      setSnackbarOpen(true);
      await loadBackupStatus();
      await loadScheduledBackupStatus();
      await loadBackupHistory();
    } catch (err) {
      setSnackbarMessage('Failed to delete backup');
      setSnackbarOpen(true);
      console.error('Failed to delete backup:', err);
    } finally {
      setDeleteConfirmOpen(false);
      setBackupToDelete(null);
    }
  };

  const handleBackupDetails = (backup: BackupRecord) => {
    setSelectedBackup(backup);
    setDetailsModalOpen(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (milliseconds?: number) => {
    if (!milliseconds) return 'Unknown';
    const seconds = Math.floor(milliseconds / 1000);
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return '1 day ago';
    return `${diffInDays} days ago`;
  };

  const getTimeRelative = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = date.getTime() - now.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    
    if (diffInMs < 0) {
      // Past date - use getTimeAgo logic
      const pastDiffInDays = Math.floor(-diffInMs / (1000 * 60 * 60 * 24));
      if (pastDiffInDays === 0) return 'Today';
      if (pastDiffInDays === 1) return '1 day ago';
      return `${pastDiffInDays} days ago`;
    }
    
    // Future date
    if (diffInDays === 0) {
      if (diffInHours < 1) return 'Soon';
      if (diffInHours === 1) return 'in 1 hour';
      return `in ${diffInHours} hours`;
    }
    if (diffInDays === 1) return 'Tomorrow';
    return `in ${diffInDays} days`;
  };

  // Filter backup history
  const filteredBackups = backupHistory?.data.filter(backup => {
    if (statusFilter !== 'All' && backup.status !== statusFilter) return false;
    if (typeFilter !== 'All' && backup.type !== typeFilter) return false;
    return true;
  }) || [];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress data-testid="backup-status-loading" />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" data-testid="backup-status-error">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Backup Management
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={() => {
            loadBackupStatus();
            loadScheduledBackupStatus();
            loadBackupHistory();
          }}
          sx={{ ml: 'auto' }}
          disabled={creatingBackup}
        >
          Refresh
        </Button>
      </Box>

      {/* Status Dashboard and Manual Backup Cards (side-by-side on desktop) */}
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} mb={3}>
        {/* Status Dashboard Card */}
        <Card sx={{ flex: 1 }}>
          <CardHeader
            title="Status Dashboard"
            action={
              <IconButton
                onClick={() => toggleCard('status')}
                aria-label="Collapse Status Dashboard"
              >
                {expandedCards.status ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            }
          />
          <Collapse in={expandedCards.status}>
            <CardContent>
              {backupStatus?.lastBackupDate ? (
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Last Backup</Typography>
                  <Typography>
                    {formatDate(backupStatus.lastBackupDate)} ({getTimeAgo(backupStatus.lastBackupDate)})
                  </Typography>
                  <Typography>
                    Type: {backupStatus.lastBackupType || 'Unknown'}
                  </Typography>
                  {backupStatus.totalSize && (
                    <Typography>Size: {formatFileSize(backupStatus.totalSize)}</Typography>
                  )}
                  
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Scheduled Backups</Typography>
                  {scheduledBackupStatus?.isRunning ? (
                    <>
                      <Typography>
                        Status: <Chip label="Running" color="success" size="small" />
                      </Typography>
                      {scheduledBackupStatus.nextRunTime && (
                        <Typography>
                          Next: {formatDate(scheduledBackupStatus.nextRunTime)} ({getTimeRelative(scheduledBackupStatus.nextRunTime)})
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography>
                      Status: <Chip label="Stopped" color="error" size="small" />
                    </Typography>
                  )}
                  {scheduledBackupStatus?.lastRunTime && (
                    <Typography>
                      Last Run: {formatDate(scheduledBackupStatus.lastRunTime)} 
                      {scheduledBackupStatus.lastRunSuccessful ? (
                        <Chip label="Success" color="success" size="small" sx={{ ml: 1 }} />
                      ) : (
                        <Chip label="Failed" color="error" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  )}
                  {scheduledBackupStatus && (
                    <Typography>
                      Total Automated: {scheduledBackupStatus.totalBackupsCreated} backups
                    </Typography>
                  )}
                  
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Storage Used</Typography>
                  <Typography>{backupHistory?.totalCount || 0} backups</Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Google Drive Status</Typography>
                  {scheduledBackupStatus?.googleDriveEnabled ? (
                    <>
                      <Typography>
                        Connection: {scheduledBackupStatus.googleDriveConnected ? (
                          <Chip label="Connected" color="success" size="small" />
                        ) : (
                          <Chip label="Disconnected" color="error" size="small" />
                        )}
                      </Typography>
                      {backupStatus?.googleDriveUploadTime && (
                        <Typography>
                          Last Upload: {formatDate(backupStatus.googleDriveUploadTime)}
                        </Typography>
                      )}
                      {backupStatus?.googleDriveFolderPath && (
                        <Typography>
                          Folder: {backupStatus.googleDriveFolderPath}
                        </Typography>
                      )}
                      {backupStatus?.googleDriveError && (
                        <Typography color="error">
                          Error: {backupStatus.googleDriveError}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography>
                      <Chip label="Disabled" color="default" size="small" />
                    </Typography>
                  )}

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>System Info</Typography>
                  <Typography>{backupStatus.familyCount} families</Typography>
                  <Typography>{backupStatus.userCount} users</Typography>
                  <Typography>{backupStatus.childCount} children</Typography>
                  
                  {backupStatus.gitBranch && (
                    <Box>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" fontWeight="bold" gutterBottom>Version Info</Typography>
                      <Typography>Branch: {backupStatus.gitBranch}</Typography>
                      {backupStatus.gitCommit && (
                        <Typography>Commit: {backupStatus.gitCommit.substring(0, 8)}</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography>No backups yet</Typography>
              )}
            </CardContent>
          </Collapse>
        </Card>

        {/* Manual Backup Card */}
        <Card sx={{ flex: 1 }}>
          <CardHeader
            title="Manual Backup"
            action={
              <IconButton
                onClick={() => toggleCard('backup')}
                aria-label="Collapse Manual Backup"
              >
                {expandedCards.backup ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            }
          />
          <Collapse in={expandedCards.backup}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create an immediate backup of the system including database and files.
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                startIcon={<CloudUpload />}
                onClick={handleCreateBackup}
                disabled={creatingBackup}
                fullWidth
                sx={{ mb: 2 }}
              >
                {creatingBackup ? 'Creating Backup...' : 'Create Backup Now'}
              </Button>
              
              {creatingBackup && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Please wait while the backup is being created...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>
      </Box>

      {/* Backup History Card */}
      <Card>
        <CardHeader
          title="Backup History"
          action={
            <IconButton
              onClick={() => toggleCard('history')}
              aria-label="Collapse History"
            >
              {expandedCards.history ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          }
        />
        <Collapse in={expandedCards.history}>
          <CardContent>
            {/* Filters */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Success">Success</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Manual">Manual</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Table */}
            {filteredBackups.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary">
                  No backups found
                </Typography>
                <Typography color="text.secondary">
                  Create your first backup
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredBackups.map((backup) => (
                        <TableRow key={backup.id} hover>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={() => handleBackupDetails(backup)}
                              sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                            >
                              {formatDate(backup.createdAt)}
                            </Button>
                          </TableCell>
                          <TableCell>{backup.type}</TableCell>
                          <TableCell>{backup.size ? formatFileSize(backup.size) : 'Unknown'}</TableCell>
                          <TableCell>
                            <Chip
                              label={backup.status}
                              color={backup.status === 'Success' ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{backup.createdBy || 'Unknown'}</TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              {backup.status === 'Success' && (
                                <Button
                                  size="small"
                                  startIcon={<Download />}
                                  onClick={() => handleDownload(backup)}
                                >
                                  Download
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => handleDeleteClick(backup)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {backupHistory && backupHistory.totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={backupHistory.totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to delete this backup?
          </Typography>
          {backupToDelete && (
            <Box>
              <Typography><strong>File:</strong> {backupToDelete.filename}</Typography>
              <Typography><strong>Size:</strong> {backupToDelete.size ? formatFileSize(backupToDelete.size) : 'Unknown'}</Typography>
              <Typography><strong>Date:</strong> {formatDate(backupToDelete.createdAt)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Details Modal */}
      <Dialog open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Backup Details</DialogTitle>
        <DialogContent>
          {selectedBackup && (
            <Box>
              <Typography mb={1}><strong>ID:</strong> {selectedBackup.id}</Typography>
              <Typography mb={1}><strong>Filename:</strong> {selectedBackup.filename}</Typography>
              <Typography mb={1}><strong>Created:</strong> {formatDate(selectedBackup.createdAt)}</Typography>
              <Typography mb={1}><strong>Size:</strong> {selectedBackup.size ? formatFileSize(selectedBackup.size) : 'Unknown'}</Typography>
              <Typography mb={1}><strong>Type:</strong> {selectedBackup.type}</Typography>
              <Typography mb={1}><strong>Status:</strong> {selectedBackup.status}</Typography>
              <Typography mb={1}><strong>Created By:</strong> {selectedBackup.createdBy || 'Unknown'}</Typography>
              {selectedBackup.duration && (
                <Typography mb={1}><strong>Duration:</strong> {formatDuration(selectedBackup.duration)}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedBackup && selectedBackup.status === 'Success' && (
            <Button
              onClick={() => handleDownload(selectedBackup)}
              startIcon={<Download />}
            >
              Download
            </Button>
          )}
          {selectedBackup && (
            <Button
              onClick={() => {
                handleDeleteClick(selectedBackup);
                setDetailsModalOpen(false);
              }}
              color="error"
              startIcon={<Delete />}
            >
              Delete
            </Button>
          )}
          <Button onClick={() => setDetailsModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default AdminSystemBackups;